import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Loader2, Activity, Brain, ShieldCheck, Package, Users, MapPin,
  CheckCircle, AlertCircle, Clock, Zap, RefreshCw, Play
} from "lucide-react";
import { Card } from "../shared/UIComponents";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGENT_META = {
  borrower_intake_agent: { label: "Borrower Intake", icon: Users, color: "#3b82f6" },
  underwriting_triage_agent: { label: "Underwriting", icon: ShieldCheck, color: "#8b5cf6" },
  deal_packaging_agent: { label: "Deal Packaging", icon: Package, color: "#f59e0b" },
  lender_match_agent: { label: "Lender Match", icon: MapPin, color: "#10b981" },
  closing_coordinator_agent: { label: "Closing", icon: CheckCircle, color: "#06b6d4" },
};

const RISK_COLORS = { low: "#10b981", moderate: "#f59e0b", elevated: "#ef4444" };
const DECISION_COLORS = { prequalify: "#10b981", manual_review: "#f59e0b", caution: "#ef4444" };

export default function AgentPerformancePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rerunning, setRerunning] = useState(null);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchMetrics = () => {
    setLoading(true);
    axios.get(`${API}/admin/agents/metrics`, { headers })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchMetrics(); }, []);

  const triggerJob = async (jobId) => {
    setRerunning(jobId);
    try {
      await axios.post(`${API}/admin/scheduler/run/${jobId}`, {}, { headers });
      setTimeout(fetchMetrics, 1000);
    } catch {}
    finally { setRerunning(null); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
  );

  if (!data) return null;

  const agentChartData = Object.entries(data.agents || {}).map(([key, val]) => ({
    name: (AGENT_META[key] || {}).label || key.replace(/_/g, " "),
    runs: val.total_runs,
    success: val.completed,
    review: val.needs_review,
    rate: val.success_rate,
    fill: (AGENT_META[key] || {}).color || "#888",
  }));

  const riskPieData = Object.entries(data.risk_distribution || {}).map(([k, v]) => ({
    name: k.charAt(0).toUpperCase() + k.slice(1), value: v, fill: RISK_COLORS[k],
  })).filter(d => d.value > 0);

  const decisionPieData = Object.entries(data.decision_counts || {}).map(([k, v]) => ({
    name: k.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase()), value: v, fill: DECISION_COLORS[k],
  })).filter(d => d.value > 0);

  return (
    <div className="space-y-6" data-testid="agent-performance-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-[#0A0A0A] flex items-center gap-2">
            <Brain className="h-5 w-5 text-purple-600" /> AI Agent Performance
          </h3>
          <p className="text-xs text-[#888] mt-1">
            {data.total_pipeline_runs} total agent runs &bull; {data.automation_events} automation events
          </p>
        </div>
        <button onClick={fetchMetrics} className="p-2 rounded-lg border border-cream-warm hover:bg-cream transition-colors" data-testid="refresh-metrics">
          <RefreshCw className="h-4 w-4 text-[#666]" />
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total Runs", value: data.total_pipeline_runs, icon: Activity, color: "text-blue-600" },
          { label: "Avg Risk Score", value: data.avg_risk_score, icon: ShieldCheck, color: "text-purple-600" },
          { label: "Avg Qualified Lenders", value: data.avg_qualified_lenders, icon: Users, color: "text-emerald-600" },
          { label: "Automation Events", value: data.automation_events, icon: Zap, color: "text-amber-600" },
        ].map(s => (
          <Card key={s.label} hover={false}>
            <div className="flex items-center gap-3" data-testid={`metric-${s.label.toLowerCase().replace(/ /g, '-')}`}>
              <div className="p-2 rounded-lg bg-cream border border-cream-warm">
                <s.icon className={`h-4 w-4 ${s.color}`} />
              </div>
              <div>
                <div className="text-xl font-bold text-[#0A0A0A]">{s.value}</div>
                <div className="text-[10px] text-[#888] uppercase tracking-wider">{s.label}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Agent Run Counts */}
        <Card hover={false}>
          <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4">Runs by Agent</h4>
          {agentChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={agentChartData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8e4dc" />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#888" }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#333" }} width={100} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e8e4dc", fontSize: 12 }} />
                <Bar dataKey="success" name="Completed" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
                <Bar dataKey="review" name="Needs Review" stackId="a" fill="#f59e0b" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-sm text-[#999]">No agent runs yet</div>
          )}
        </Card>

        {/* Risk Distribution + Decisions */}
        <Card hover={false}>
          <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4">Underwriting Insights</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] text-[#888] uppercase tracking-wider text-center mb-2">Risk Distribution</p>
              {riskPieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={140}>
                  <PieChart>
                    <Pie data={riskPieData} dataKey="value" cx="50%" cy="50%" innerRadius={30} outerRadius={55} paddingAngle={2}>
                      {riskPieData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e8e4dc", fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <div className="text-center py-8 text-xs text-[#999]">No data</div>}
              <div className="flex justify-center gap-3 mt-1">
                {riskPieData.map(d => (
                  <span key={d.name} className="flex items-center gap-1 text-[10px] text-[#666]">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: d.fill }} /> {d.name} ({d.value})
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] text-[#888] uppercase tracking-wider text-center mb-2">Decisions</p>
              {decisionPieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={140}>
                  <PieChart>
                    <Pie data={decisionPieData} dataKey="value" cx="50%" cy="50%" innerRadius={30} outerRadius={55} paddingAngle={2}>
                      {decisionPieData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e8e4dc", fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <div className="text-center py-8 text-xs text-[#999]">No data</div>}
              <div className="flex justify-center gap-3 mt-1">
                {decisionPieData.map(d => (
                  <span key={d.name} className="flex items-center gap-1 text-[10px] text-[#666]">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: d.fill }} /> {d.name} ({d.value})
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Agent Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(AGENT_META).map(([key, meta]) => {
          const agent = (data.agents || {})[key] || {};
          const Icon = meta.icon;
          return (
            <Card key={key} hover={false}>
              <div className="text-center" data-testid={`agent-card-${key}`}>
                <div className="mx-auto w-10 h-10 rounded-xl flex items-center justify-center mb-2" style={{ backgroundColor: meta.color + "15" }}>
                  <Icon className="h-5 w-5" style={{ color: meta.color }} />
                </div>
                <div className="text-sm font-bold text-[#0A0A0A]">{meta.label}</div>
                <div className="text-2xl font-bold mt-1" style={{ color: meta.color }}>{agent.total_runs || 0}</div>
                <div className="text-[10px] text-[#888]">runs</div>
                <div className="mt-2 text-xs">
                  <span className="text-emerald-600 font-medium">{agent.success_rate || 0}%</span>
                  <span className="text-[#aaa] ml-1">success</span>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Scheduler Jobs */}
      <Card hover={false}>
        <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4">Automation Scheduler</h4>
        <div className="space-y-2">
          {(data.scheduler_jobs || []).map(job => (
            <div key={job.id} className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-cream border border-cream-warm" data-testid={`scheduler-job-${job.id}`}>
              <div className="flex items-center gap-3">
                <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                <div>
                  <div className="text-sm font-medium text-[#0A0A0A]">{job.id.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</div>
                  <div className="text-[10px] text-[#888]">{job.trigger}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {job.next_run && (
                  <span className="text-[10px] text-[#888] flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {new Date(job.next_run).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
                <button onClick={() => triggerJob(job.id)} disabled={rerunning === job.id}
                  data-testid={`run-job-${job.id}`}
                  className="p-1.5 rounded-lg border border-cream-warm hover:bg-emerald-50 hover:border-emerald-200 transition-colors disabled:opacity-50">
                  {rerunning === job.id
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-600" />
                    : <Play className="h-3.5 w-3.5 text-emerald-600" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Recent Agent Activity */}
      <Card hover={false}>
        <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4">Recent Agent Activity</h4>
        <div className="space-y-1.5">
          {(data.recent_runs || []).map((run, i) => {
            const meta = AGENT_META[run.agent_name] || {};
            const Icon = meta.icon || Activity;
            return (
              <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
                className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-cream/50 transition-colors">
                <Icon className="h-4 w-4 shrink-0" style={{ color: meta.color || "#888" }} />
                <span className="text-sm text-[#333] font-medium">{meta.label || run.agent_name}</span>
                <span className="text-xs text-[#888] truncate flex-1">{run.entity_id?.slice(0, 8)}...</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  run.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                }`}>{run.status}</span>
                <span className="text-[10px] text-[#aaa]">
                  {run.started_at ? new Date(run.started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                </span>
              </motion.div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
